/* Stockist map section: list, filters, search, Mapbox map and profile sheet.
   Ported from wireframes/stockist-map-v2.html. One instance per [data-smap] root.
   Loads in Node without a DOM (tests require the pure helpers exported at the end). */
(function () {
  'use strict';

  /* ================= Pure helpers (exported for tests) ================= */
  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371, rad = function (x) { return x * Math.PI / 180; };
    var dLat = rad(lat2 - lat1), dLng = rad(lng2 - lng1);
    var h = Math.pow(Math.sin(dLat / 2), 2) + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.pow(Math.sin(dLng / 2), 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  /* "west,south,east,north;west,south,east,north" -> [[w,s,e,n], ...] */
  function parseBoxes(str) {
    return String(str == null ? '' : str).split(';')
      .map(function (s) { return s.trim(); })
      .filter(Boolean)
      .map(function (s) { return s.split(',').map(function (n) { return Number(n.trim()); }); })
      .filter(function (b) { return b.length === 4 && b.every(function (n) { return isFinite(n); }); });
  }

  /* The record's own region key wins when it is a known region; otherwise the first region whose boxes contain it. */
  function regionFor(r, regions) {
    if (r && r.region && regions.some(function (x) { return x.key === r.region; })) return r.region;
    if (!r || typeof r.lat !== 'number' || typeof r.lng !== 'number' || !isFinite(r.lat) || !isFinite(r.lng)) return null;
    for (var i = 0; i < regions.length; i++) {
      var boxes = regions[i].boxes || [];
      for (var j = 0; j < boxes.length; j++) {
        var b = boxes[j];
        if (r.lng >= b[0] && r.lat >= b[1] && r.lng <= b[2] && r.lat <= b[3]) return regions[i].key;
      }
    }
    return null;
  }

  function fillTemplate(tpl, vars) {
    // [key] is the settings syntax (Shopify rejects {{ }} in JSON template values); {{ key }} is still accepted.
    return String(tpl == null ? '' : tpl).replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, function (m, a, b) {
      var k = a || b;
      return vars && Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    });
  }

  /* ================= Browser-only code ================= */
  var DEFAULT_STRINGS = {
    label_directions: 'Directions',
    label_details: 'Details',
    label_view: 'View shop',
    label_show_all: 'Show all',
    msg_map_failed: 'The map couldn’t load. The list below still works.',
    msg_geo_denied: 'We couldn’t get your location. Try a suburb or postcode instead.',
    msg_no_blurb: '',
    msg_geo_error: 'We couldn’t get your location. Try a suburb or postcode instead.',
    msg_geo_unsupported: 'Your browser doesn’t support location. Search for a town instead.',
    msg_searching: 'Searching…',
    msg_locating: 'Finding you…',
    msg_search_empty: 'Type a suburb, town or postcode to search.',
    msg_not_found: 'Couldn’t find “[query]”. Try a nearby town or a postcode.',
    msg_search_failed: 'Search is down right now. Try a region filter or “Use my location”.',
    msg_nearest: 'Nearest to [place].',
    label_clear_location: 'Clear location',
    label_your_location: 'your location',
    label_you: 'You',
    label_website: 'Visit website',
    label_brand_page: 'Shop [brand] here',
    brand_name: '',
    label_email: 'Email',
    label_instagram: 'Instagram',
    label_address: 'Address',
    label_location: 'Location',
    label_brands: 'Brands stocked',
    label_products: 'Products stocked',
    label_contact_tbc: 'Contact details to be added',
    label_new_tab: '(opens in new tab)',
    label_for: 'for [name]',
    label_to: 'to [name]',
    label_list: 'List',
    label_map: 'Map',
    label_jump_list: 'Jump to list',
    label_jump_map: 'Jump to map',
    label_logo: '[name] logo',
    type_retailer: 'Retailer',
    type_distributor: 'Distributor',
    type_online: 'Online store',
    legend_stockist: 'Stockist',
    result_one: '[count] shop',
    result_other: '[count] shops',
    result_of: '[count] of [total] shops',
    result_in_region: ' in [region]',
    result_nearest: ', nearest first',
    result_intl: '[count] stockists in [countries] countries',
    result_none: 'No results.',
    brand_sticky_johnson: 'Sticky Johnson',
    brand_griptec: 'Griptec',
    brand_surfscreen: 'SurfScreen'
  };

  var ICON_EXT = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" focusable="false"><path d="M4 2h6v6M10 2L3 9"/></svg>';
  var ICON_LIST = '<path d="M2 4h12M2 8h12M2 12h12"/>';
  var ICON_MAP = '<rect x="2" y="2" width="12" height="12"/><path d="M8 5v6M5 8h6"/>';

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var num = function (v) {
    if (v === null || v === undefined || v === '') return null;
    var n = Number(v);
    return isFinite(n) ? n : null;
  };
  var bool = function (v) { return v === true || v === 'true'; };
  var list = function (s) {
    return String(s || '').split(',').map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
  };
  var lngLat = function (s, fallback) {
    var p = String(s || '').split(',').map(function (x) { return Number(x.trim()); });
    return p.length === 2 && p.every(function (n) { return isFinite(n); }) ? p : fallback;
  };
  var safeUrl = function (u) {
    if (!u) return '';
    try { var p = new URL(String(u), location.href).protocol; return p === 'https:' || p === 'http:' || p === 'mailto:' ? String(u) : ''; }
    catch (e) { return ''; }
  };
  var hasCoords = function (r) { return r.lat !== null && r.lng !== null; };
  var readJson = function (el, fallback) {
    if (!el) return fallback;
    try { var v = JSON.parse(el.textContent); return v == null ? fallback : v; }
    catch (e) { console.warn('Stockist map: could not parse', el, e); return fallback; }
  };

  var instances = new WeakMap();

  function createInstance(root) {
    var ac = new AbortController();
    var on = function (el, type, fn, opts) {
      if (el) el.addEventListener(type, fn, Object.assign({ signal: ac.signal }, opts || {}));
    };
    var q = function (s) { return root.querySelector(s); };
    var destroyed = false;

    /* ---------- Config, strings, data ---------- */
    var ds = root.dataset;
    var cfg = {
      token: (ds.token || '').trim(),
      style: ds.style || 'mapbox://styles/mapbox/light-v11',
      nzCenter: lngLat(ds.nzCenter, [173.5, -41.0]),
      nzZoom: num(ds.nzZoom) !== null ? num(ds.nzZoom) : 4.5,
      worldCenter: lngLat(ds.worldCenter, [20, 10]),
      worldZoom: num(ds.worldZoom) !== null ? num(ds.worldZoom) : 1.2,
      unit: ds.unit === 'mi' ? 'mi' : 'km',
      showIntl: bool(ds.showInternational),
      defaultTab: ds.defaultTab || ''
    };
    var T = Object.assign({}, DEFAULT_STRINGS);
    var i18n = readJson(q('[data-smap-i18n]'), {});
    Object.keys(i18n).forEach(function (k) {
      if (typeof i18n[k] === 'string' && (i18n[k] !== '' || k === 'msg_no_blurb')) T[k] = i18n[k];
    });
    var t = function (key, vars) { return fillTemplate(T[key] == null ? '' : T[key], vars || {}); };

    var records = readJson(q('[data-smap-data]'), []);
    if (!Array.isArray(records)) records = [];
    records = records.filter(function (r) { return r && r.id; }).map(function (r) {
      return Object.assign({}, r, {
        id: String(r.id),
        name: String(r.name || ''),
        type: r.type || 'retailer',
        country: r.country || '',
        town_city: r.town_city || '',
        lat: num(r.lat),
        lng: num(r.lng),
        brands: Array.isArray(r.brands) ? r.brands : [],
        website: safeUrl(r.website),
        instagram: safeUrl(r.instagram),
        brand_page_url: safeUrl(r.brand_page_url),
        google_maps_link: safeUrl(r.google_maps_link),
        products: (Array.isArray(r.products) ? r.products : []).map(function (p) { return p ? Object.assign({}, p, { url: safeUrl(p.url) }) : p; })
      });
    });
    var BY_ID = new Map(records.map(function (r) { return [r.id, r]; }));

    if (window.Shopify && window.Shopify.designMode) {
      var noCoords = records.filter(function (r) { return !hasCoords(r); }).map(function (r) { return r.name; });
      if (noCoords.length) console.info('Stockist map: ' + noCoords.length + ' stockist(s) without coordinates (list only, no pin): ' + noCoords.join(', '));
    }

    /* ---------- Elements ---------- */
    var el = {
      mapPane: q('[data-smap-map-pane]'),
      map: q('[data-smap-map]'),
      loading: q('[data-smap-loading]'),
      fail: q('[data-smap-fail]'),
      panel: q('[data-smap-panel]'),
      list: q('[data-smap-list]'),
      empty: q('[data-smap-empty]'),
      showAll: q('[data-smap-show-all]'),
      result: q('[data-smap-result]'),
      count: q('[data-smap-count]'),
      overseas: q('[data-smap-overseas]'),
      form: q('[data-smap-search]'),
      input: q('[data-smap-search-input]'),
      submit: q('[data-smap-search-submit]'),
      locate: q('[data-smap-locate]'),
      status: q('[data-smap-status]'),
      regions: q('[data-smap-regions]'),
      controls: q('[data-smap-controls]'),
      tabs: q('[data-smap-tabs]'),
      sheet: q('[data-smap-sheet]'),
      sheetClose: q('[data-smap-sheet-close]'),
      sheetMedia: q('[data-smap-sheet-media]'),
      sheetBody: q('[data-smap-sheet-body]'),
      sheetActions: q('[data-smap-sheet-actions]')
    };
    var sheetTitleId = 'smap-title-' + (ds.sectionId || Math.random().toString(36).slice(2));

    /* ---------- Tabs and regions from the markup ---------- */
    /* Scoped to their containers: server-rendered rows also carry data-region. */
    var within = function (box, s) { return box ? Array.prototype.slice.call(box.querySelectorAll(s)) : []; };
    var tabs = within(el.tabs, '[data-tab]').map(function (b) {
      var types = list(b.dataset.types);
      return { key: b.dataset.tab, el: b, types: types, countries: list(b.dataset.countries), local: !types.length || types.indexOf('retailer') !== -1 };
    });
    if (!tabs.length) tabs = [{ key: 'all', el: null, types: [], countries: [], local: true }];
    var tabByKey = function (k) { return tabs.filter(function (x) { return x.key === k; })[0] || null; };
    var matchesTab = function (r, tab) {
      return (!tab.types.length || tab.types.indexOf(r.type) !== -1) &&
        (!tab.countries.length || tab.countries.indexOf(String(r.country).toLowerCase()) !== -1);
    };

    var chips = within(el.regions, '[data-region]');
    var regions = chips.filter(function (c) { return c.dataset.region; }).map(function (c) {
      var countEl = c.querySelector('[data-smap-region-count]');
      var label = Array.prototype.filter.call(c.childNodes, function (n) { return n !== countEl; })
        .map(function (n) { return n.textContent; }).join('').trim();
      return { key: c.dataset.region, label: label, boxes: parseBoxes(c.dataset.bbox), el: c };
    });
    var regionLabel = function (k) { var r = regions.filter(function (x) { return x.key === k; })[0]; return r ? r.label : k; };

    /* ---------- State ---------- */
    /* Hash: #stockist=<id> beats #tab=<key> beats a bare #<region-key>. */
    var isRegion = function (k) { return regions.some(function (x) { return x.key === k; }); };
    var firstLocalTab = function () { return tabs.filter(function (x) { return x.local; })[0] || null; };
    function parseHash() {
      var h = location.hash.replace(/^#/, ''), m;
      var dec = function (v) { try { return decodeURIComponent(v); } catch (e) { return v; } };
      if ((m = h.match(/^stockist=(.+)$/))) return { stockist: dec(m[1]) };
      if ((m = h.match(/^tab=(.+)$/))) return { tab: dec(m[1]) };
      if (h && isRegion(dec(h))) return { region: dec(h) };
      return {};
    }
    /* ?stockist= / ?tab= / ?region= (redirect targets may drop the hash): same precedence; read on load only, never rewritten. */
    function parseQuery() {
      var p;
      try { p = new URLSearchParams(location.search); } catch (e) { return {}; }
      if (p.get('stockist')) return { stockist: p.get('stockist') };
      if (p.get('tab')) return { tab: p.get('tab') };
      if (p.get('region') && isRegion(p.get('region'))) return { region: p.get('region') };
      return {};
    }
    /* On load a hash wins over the query. */
    function parseLoad() { var h = parseHash(); return Object.keys(h).length ? h : parseQuery(); }
    var H0 = parseLoad();
    var initialTab = tabByKey(H0.tab) || (H0.region && firstLocalTab()) || tabByKey(cfg.defaultTab) ||
      tabs.filter(function (x) { return x.el && x.el.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
    var state = {
      tab: initialTab.key,
      region: H0.region && initialTab.local ? H0.region : '',
      origin: null,
      dist: new Map(),
      hl: null
    };
    var curTab = function () { return tabByKey(state.tab) || tabs[0]; };

    var DESKTOP_MQ = window.matchMedia('(min-width: 900px)');
    var MOBILE_MQ = window.matchMedia('(max-width: 899px)');
    var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* ---------- Formatting ---------- */
    var fmtDist = function (km) {
      var v = cfg.unit === 'mi' ? km * 0.621371 : km;
      return (v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString()) + ' ' + cfg.unit;
    };
    var typeLabel = function (type) { return T['type_' + type] || type; };
    var brandLabel = function (b) {
      return T['brand_' + b] || String(b).replace(/[_-]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    };
    var directionsUrl = function (r) {
      return r.google_maps_link || ('https://www.google.com/maps/dir/?api=1&destination=' + r.lat + ',' + r.lng);
    };
    var hasDirections = function (r) { return !!r.google_maps_link || (r.type === 'retailer' && hasCoords(r)); };
    var initialOf = function (r) { return esc((r.name || '?').trim().charAt(0).toUpperCase()); };

    /* Broken logo -> initial placeholder (capture phase: error events don't bubble) */
    on(root, 'error', function (e) {
      var img = e.target;
      if (!img || img.tagName !== 'IMG') return;
      var span = document.createElement('span');
      span.setAttribute('aria-hidden', 'true');
      if (img.dataset.initial !== undefined) {
        span.className = 'smap__initial';
        span.textContent = img.dataset.initial;
      } else if (img.parentNode && img.parentNode.classList && img.parentNode.classList.contains('smap__row-media')) {
        var li = img.closest('[data-stockist-id]');
        var nm = li && li.querySelector('.smap__row-name');
        span.className = 'smap__row-initial';
        span.textContent = nm ? nm.textContent.trim().charAt(0).toUpperCase() : '';
      } else return;
      img.replaceWith(span);
    }, { capture: true });

    /* ---------- Rows: adopt the server-rendered <li>s; build any missing ones from the JSON ---------- */
    /* Same shape as the section's Liquid row. */
    function buildRow(r) {
      var li = document.createElement('li');
      li.className = 'smap__row';
      li.id = 'stockist-' + r.id;
      li.setAttribute('data-stockist-id', r.id);
      li.setAttribute('data-type', r.type);
      li.setAttribute('data-region', r.region || '');
      li.setAttribute('data-country', r.country);
      var isNz = !r.country || r.country === 'New Zealand';
      // Only the non-empty parts, joined with a middle dot (no leading separator when the town is blank).
      var parts = [];
      if (r.town_city) parts.push('<span class="smap__row-town">' + esc(r.town_city) + '</span>');
      if (r.address) parts.push('<span class="smap__row-address">' + esc(r.address) + '</span>');
      if (!isNz && r.country) parts.push('<span class="smap__row-country">' + esc(r.country) + '</span>');
      if (r.type !== 'retailer' && r.email) parts.push('<span class="smap__row-email">' + esc(r.email) + '</span>');
      if (!parts.length) parts.push('<span class="smap__row-type">' + esc(typeLabel(r.type)) + '</span>');   // e.g. an online-only store
      var meta = parts.join(' · ') + '<span class="smap__row-distance" data-smap-distance></span>';
      var media = r.logo
        ? '<img src="' + esc(r.logo) + '" alt="" width="56" height="56" loading="lazy">'
        : '<span class="smap__row-initial" aria-hidden="true">' + initialOf(r) + '</span>';
      li.innerHTML =
        '<div class="smap__row-media">' + media + '</div>' +
        '<div class="smap__row-main">' +
          '<h3 class="smap__row-name">' + (r.website ? '<a href="' + esc(r.website) + '" rel="noopener" target="_blank">' + esc(r.name) + '</a>' : esc(r.name)) + '</h3>' +
          '<p class="smap__row-meta">' + meta + '</p>' +
          '<div class="smap__row-actions">' +
            (hasDirections(r) ? '<a class="smap__btn smap__btn--secondary" data-smap-directions href="' + esc(directionsUrl(r)) + '" rel="noopener" target="_blank">' + esc(t('label_directions')) + '</a>' : '') +
            '<button type="button" class="smap__btn smap__btn--secondary" data-smap-details>' + esc(t('label_details')) + '</button>' +
          '</div>' +
        '</div>';
      return li;
    }

    var rowById = new Map();
    within(el.list, 'li[data-stockist-id]').forEach(function (li) {
      var id = li.getAttribute('data-stockist-id');
      rowById.set(id, li);
      if (!BY_ID.has(id)) {       // a row with no JSON entry still lists, filtered by its own attributes
        var nameEl = li.querySelector('.smap__row-name');
        var rec = { id: id, name: nameEl ? nameEl.textContent.trim() : id, type: li.getAttribute('data-type') || 'retailer',
          country: li.getAttribute('data-country') || '', town_city: '', region: li.getAttribute('data-region') || '',
          lat: num(li.getAttribute('data-lat')), lng: num(li.getAttribute('data-lng')), brands: [], products: [] };
        records.push(rec);
        BY_ID.set(id, rec);
      }
    });
    records.forEach(function (r) {
      var li = rowById.get(r.id);
      if (!r.region && li && li.getAttribute('data-region')) r.region = li.getAttribute('data-region');
      if (!li && el.list) { li = buildRow(r); rowById.set(r.id, li); el.list.appendChild(li); }
      if (!li) return;
      Array.prototype.forEach.call(li.querySelectorAll('a[href]'), function (a) { if (!safeUrl(a.getAttribute('href'))) a.removeAttribute('href'); });
      var dir = li.querySelector('[data-smap-directions]');
      if (dir) {
        var href = dir.getAttribute('href');
        if (!hasDirections(r)) dir.hidden = true;            // e.g. a distributor whose only point is a country centroid
        else if (!href || href === '#') dir.setAttribute('href', directionsUrl(r));
      }
    });

    records.forEach(function (r) {
      r._region = regionFor(r, regions);
      r._pos = hasCoords(r) ? [r.lng, r.lat] : null;
    });
    /* Non-retail records that share a point (country centroids) fan out so each pin is clickable. */
    var groups = {};
    records.filter(function (r) { return r.type !== 'retailer' && r._pos; }).forEach(function (r) {
      var k = r.lat + ',' + r.lng; (groups[k] = groups[k] || []).push(r);
    });
    Object.keys(groups).forEach(function (k) {
      var g = groups[k];
      if (g.length < 2) return;
      g.forEach(function (r, i) {
        var a = 2 * Math.PI * i / g.length, rad = 1.6;
        r._pos = [r.lng + rad * Math.cos(a), r.lat + rad * Math.sin(a)];
      });
    });

    /* ---------- Selectors ---------- */
    var tabRecords = function (tab) { return records.filter(function (r) { return matchesTab(r, tab || curTab()); }); };
    var byName = function (a, b) { return a.name.localeCompare(b.name); };
    function visible() {
      var tab = curTab();
      var rows = tabRecords(tab);
      if (tab.local && state.region) rows = rows.filter(function (r) { return r._region === state.region; });
      if (state.origin) {
        rows.sort(function (a, b) {
          var da = state.dist.has(a.id) ? state.dist.get(a.id) : Infinity;
          var db = state.dist.has(b.id) ? state.dist.get(b.id) : Infinity;
          return (da - db) || byName(a, b);
        });
      } else if (tab.local) {
        rows.sort(byName);                                  // A-Z; distance order after a search or locate
      } else {
        rows.sort(function (a, b) { return a.country.localeCompare(b.country) || byName(a, b); });
      }
      return rows;
    }

    /* ================= Rendering ================= */
    function renderHero() {
      var retail = records.filter(function (r) { return r.type === 'retailer'; }).length;
      var dist = records.filter(function (r) { return r.type === 'distributor'; }).length;
      var online = records.filter(function (r) { return r.type === 'online'; }).length;
      if (el.count) el.count.textContent = retail > 0 ? fillTemplate(el.count.dataset.template || '', { count: retail, distributors: dist, online: online }) : '';
      if (el.overseas) {
        var hasIntlTab = tabs.some(function (x) { return !x.local; });
        el.overseas.textContent = hasIntlTab && (dist + online) > 0
          ? fillTemplate(el.overseas.dataset.template || '', { count: retail, distributors: dist, online: online }) : '';
      }
    }

    function renderControls() {
      var tab = curTab();
      tabs.forEach(function (x) {
        if (!x.el) return;
        var sel = x.key === state.tab;
        x.el.setAttribute('aria-selected', String(sel));
        x.el.tabIndex = sel ? 0 : -1;
      });
      if (el.panel && tab.el) el.panel.setAttribute('aria-labelledby', tab.el.id);

      var pool = tabRecords(tab);
      if (el.regions) el.regions.hidden = !tab.local || !pool.length || !regions.length;
      var counts = {};
      pool.forEach(function (r) { if (r._region) counts[r._region] = (counts[r._region] || 0) + 1; });
      chips.forEach(function (c) {
        var key = c.dataset.region;
        c.setAttribute('aria-pressed', String(key === state.region));
        var n = c.querySelector('[data-smap-region-count]');
        if (n) n.textContent = key ? (counts[key] || 0) : pool.length;
      });
    }

    function renderStatus(msg, isErr) {
      if (!el.status) return;
      var html = '';
      if (msg) html += '<span class="' + (isErr ? 'smap__err' : '') + '">' + esc(msg) + '</span>';
      if (state.origin) {
        html += '<span>' + fillTemplate(esc(T.msg_nearest), { place: '<b>' + esc(state.origin.label) + '</b>' }) + '</span>' +
          ' <button type="button" class="smap__link" data-act="clear-origin">' + esc(t('label_clear_location')) + '</button>';
      }
      el.status.innerHTML = html;
    }

    /* Distance and highlight are the only per-render changes to a row's own markup. */
    function paintRow(li, r) {
      var d = state.origin && state.dist.has(r.id) ? state.dist.get(r.id) : null;
      var distEl = li.querySelector('[data-smap-distance], .smap__row-distance');
      if (distEl) distEl.textContent = d != null ? fmtDist(d) : '';
    }

    var lastListKey = null;
    function renderList() {
      if (!el.list) return;
      var tab = curTab(), rows = visible(), total = tabRecords(tab).length;
      var grouped = !tab.local && !state.origin;
      var key = state.tab + '|' + state.region + '|' + (grouped ? 'g' : '') + rows.map(function (r) { return r.id; }).join(',');
      var entering = key !== lastListKey;
      var result = '';

      if (entering) {
        // Reorder only when the visible set or order changed, so a focused row is never moved needlessly.
        within(el.list, '.smap__group').forEach(function (g) { g.remove(); });
        var frag = document.createDocumentFragment();
        var shown = new Set();
        var i = 0;
        var place = function (r) {
          var li = rowById.get(r.id);
          if (!li) return;
          li.hidden = false;
          li.style.setProperty('--i', Math.min(i++, 12));
          frag.appendChild(li);
          shown.add(r.id);
        };
        if (grouped) {
          var byCountry = new Map();
          rows.forEach(function (r) { if (!byCountry.has(r.country)) byCountry.set(r.country, []); byCountry.get(r.country).push(r); });
          byCountry.forEach(function (g, country) {
            var h = document.createElement('li');
            h.className = 'smap__group';
            h.style.setProperty('--i', Math.min(i++, 12));
            h.innerHTML = '<h3 class="smap__group-h">' + esc(country) + ' <span class="smap__n" aria-hidden="true">' + g.length + '</span></h3>';
            frag.appendChild(h);
            g.forEach(place);
          });
        } else {
          rows.forEach(place);
        }
        rowById.forEach(function (li, id) { if (!shown.has(id)) { li.hidden = true; frag.appendChild(li); } });
        el.list.appendChild(frag);
      }
      rows.forEach(function (r) { var li = rowById.get(r.id); if (li) paintRow(li, r); });

      if (!rows.length) {
        result = esc(t('result_none'));
      } else if (tab.local) {
        var n = '<strong>' + rows.length + '</strong>';
        result = rows.length === total
          ? fillTemplate(esc(rows.length === 1 ? T.result_one : T.result_other), { count: n })
          : fillTemplate(esc(T.result_of), { count: n, total: total });
        if (state.region) result += esc(t('result_in_region', { region: regionLabel(state.region) }));
        if (state.origin) result += esc(t('result_nearest'));
      } else {
        result = fillTemplate(esc(T.result_intl), { count: '<strong>' + rows.length + '</strong>', countries: new Set(rows.map(function (r) { return r.country; })).size });
        if (state.origin) result += esc(t('result_nearest'));
      }
      if (el.result) el.result.innerHTML = result;
      lastListKey = key;
      el.list.classList.toggle('is-entering', entering);
      el.list.hidden = !rows.length;
      if (el.empty) {
        el.empty.hidden = !!rows.length;
        el.empty.classList.toggle('is-entering', entering && !rows.length);
        if (el.showAll) el.showAll.hidden = !state.region;
      }
      if (state.hl) setHighlight(state.hl);
    }

    function render() { renderHero(); renderControls(); renderStatus(); renderList(); updateMapData(); }

    /* ================= Highlight (row <-> pin) ================= */
    function setHighlight(id) {
      state.hl = id;
      rowById.forEach(function (li, rid) { li.classList.toggle('smap__row--active', !!id && rid === id); });
      syncMarkerStates();
    }

    /* ================= Map ================= */
    var map = null, mapReady = false, mapFailed = false, popup = null, popupRecId = null, youMarker = null, failTimer = null;
    var onScreen = new Map();
    var pinCache = new Map();
    var ghost = null, legend = null, dataVersion = 0;
    var popupId = function () { return popup && popup.isOpen() ? popupRecId : null; };
    var mapRows = function () { return visible().filter(function (r) { return r._pos; }); };

    function pointFeature(r) {
      return { type: 'Feature', geometry: { type: 'Point', coordinates: r._pos }, properties: { id: r.id } };
    }
    function fc(rows) { return { type: 'FeatureCollection', features: rows.map(pointFeature) }; }

    function mapFail(reason) {
      if (mapFailed) return;
      mapFailed = true; mapReady = false;
      clearTimeout(failTimer);
      console.warn('Stockist map: map unavailable:', reason);
      try { if (map) map.remove(); } catch (e) { /* already gone */ }
      map = null;
      root.classList.add('smap--no-map');
      if (el.mapPane) el.mapPane.hidden = true;
      if (el.fail) {
        el.fail.textContent = t('msg_map_failed');
        if (el.panel && el.list && el.fail.parentNode !== el.panel) el.panel.insertBefore(el.fail, el.list);
        el.fail.hidden = false;
      }
      removeToggle();
    }

    function viewFor(tab) {
      var w = el.map ? el.map.clientWidth : 800;
      var fit = Math.min(0, Math.log2(Math.max(w, 1) / 700));   // narrow maps zoom out so the whole view fits
      return tab.local ? { center: cfg.nzCenter, zoom: cfg.nzZoom + fit } : { center: cfg.worldCenter, zoom: cfg.worldZoom + fit };
    }

    function initMap() {
      var mb = window.mapboxgl;
      if (!el.map || !el.mapPane) return mapFail('no map element');
      if (typeof mb === 'undefined') return mapFail('mapbox-gl.js did not load');
      if (!cfg.token) return mapFail('no Mapbox token');
      try {
        if (typeof mb.supported === 'function' && !mb.supported()) return mapFail('WebGL not supported');
        mb.accessToken = cfg.token;
        var v = viewFor(curTab());
        // Mercator: a flat world map on the international view (the default globe crops at low zoom).
        map = new mb.Map({ container: el.map, style: cfg.style, center: v.center, zoom: v.zoom, projection: 'mercator', attributionControl: true });
        map.addControl(new mb.NavigationControl({ showCompass: false }), 'top-right');
      } catch (e) { return mapFail(e && e.message); }

      failTimer = setTimeout(function () { if (!mapReady) mapFail('timeout'); }, 20000);
      map.on('error', function (e) {
        var msg = (e && e.error && e.error.message) || 'unknown';
        if (!mapReady) mapFail(msg);
        else console.warn('Stockist map: map warning:', msg);   // tiles etc. after load: non-fatal
      });
      map.on('load', function () {
        clearTimeout(failTimer);
        if (mapFailed || destroyed) return;
        try { setupLayers(); } catch (e) { return mapFail(e && e.message); }
        mapReady = true;
        if (el.loading) el.loading.hidden = true;
        updateMapData();
        applyTabView(false);
        if (state.origin) placeYou();
      });
    }

    function setupLayers() {
      // Clustering comes from the GeoJSON source; the visible pins are HTML markers styled by the theme CSS.
      // This transparent layer keeps the source's tiles loaded so querySourceFeatures has data.
      map.addSource('smap', { type: 'geojson', data: fc([]), cluster: true, clusterRadius: 48, clusterMaxZoom: 12 });
      map.addLayer({ id: 'smap-proxy', type: 'circle', source: 'smap', paint: { 'circle-radius': 1, 'circle-opacity': 0, 'circle-stroke-width': 0 } });
      map.on('render', updateMarkers);
      map.on('click', function (e) {
        var tg = e.originalEvent && e.originalEvent.target;
        if (tg && tg.closest && tg.closest('.smap-mk, .smap-cl, .mapboxgl-popup')) return;
        if (popup) popup.remove();
      });
      legend = document.createElement('div');
      legend.className = 'smap__legend';
      legend.setAttribute('aria-hidden', 'true');
      el.mapPane.appendChild(legend);
      renderLegend();
    }

    function pinClass(r) {
      return 'smap-mk' + (r.type === 'online' ? ' smap-mk--online' : '');
    }
    function makePin(r) {
      var node = document.createElement('div');
      node.className = pinClass(r);
      node.dataset.id = r.id;
      node.setAttribute('aria-hidden', 'true');
      node.innerHTML = '<span class="smap-mk__pin"></span>';
      node.addEventListener('click', function (e) { e.stopPropagation(); openPopup(r); });
      node.addEventListener('mouseenter', function () { if (r.id !== state.hl) setHighlight(r.id); });
      node.addEventListener('mouseleave', function () { setHighlight(popupId()); });
      return { marker: new window.mapboxgl.Marker({ element: node, anchor: 'center' }).setLngLat(r._pos), el: node, id: r.id };
    }
    function makeCluster(f) {
      var p = f.properties, n = p.point_count;
      var node = document.createElement('div');
      node.className = 'smap-cl ' + (n >= 30 ? 'smap-cl--l' : n >= 10 ? 'smap-cl--m' : 'smap-cl--s');
      node.setAttribute('aria-hidden', 'true');
      node.innerHTML = '<span class="smap-cl__box">' + esc(p.point_count_abbreviated) + '</span>';
      node.addEventListener('click', function (e) {
        e.stopPropagation();
        var src = map && map.getSource('smap');
        if (!src) return;
        src.getClusterExpansionZoom(p.cluster_id, function (err, zoom) {
          if (err || !map) return;
          map.easeTo({ center: f.geometry.coordinates, zoom: zoom + 0.2, duration: REDUCED.matches ? 0 : 500 });
        });
      });
      return { marker: new window.mapboxgl.Marker({ element: node, anchor: 'center' }).setLngLat(f.geometry.coordinates), el: node, id: null };
    }
    function pinFor(r) {
      var m = pinCache.get(r.id);
      if (!m) { m = makePin(r); pinCache.set(r.id, m); }
      return m;
    }

    function updateMarkers() {
      if (!mapReady || !map) return;
      var next = new Map();
      if (curTab().local) {
        if (!map.getSource('smap') || !map.isSourceLoaded('smap')) return;
        map.querySourceFeatures('smap').forEach(function (f) {
          var p = f.properties;
          if (p.cluster) {
            var ck = 'c' + dataVersion + ':' + p.cluster_id;
            if (!next.has(ck)) next.set(ck, onScreen.get(ck) || makeCluster(f));
          } else {
            var r = BY_ID.get(p.id);
            if (r && !next.has('p' + r.id)) next.set('p' + r.id, pinFor(r));
          }
        });
      } else {
        mapRows().forEach(function (r) { next.set('p' + r.id, pinFor(r)); });
      }
      var changed = false;
      onScreen.forEach(function (m, k) { if (!next.has(k)) { m.marker.remove(); changed = true; } });
      next.forEach(function (m, k) { if (!onScreen.has(k)) { m.marker.addTo(map); changed = true; } });
      onScreen = next;
      if (changed) syncMarkerStates();
    }

    function syncMarkerStates() {
      if (!mapReady || !map) return;
      var active = popupId();
      var hlVisible = false;
      onScreen.forEach(function (m) {
        if (!m.id) return;
        var r = BY_ID.get(m.id);
        var hl = m.id === state.hl, act = m.id === active;
        if (hl) hlVisible = true;
        m.el.classList.toggle('smap-mk--hl', hl);
        m.el.classList.toggle('smap-mk--active', act);
        m.el.style.zIndex = act ? 3 : hl ? 2 : '';
      });
      // The highlighted record sits inside a cluster: show a stand-in pin at its position.
      var r = state.hl && BY_ID.get(state.hl);
      if (r && r._pos && !hlVisible && matchesTab(r, curTab())) {
        if (!ghost) {
          var node = document.createElement('div');
          node.setAttribute('aria-hidden', 'true');
          ghost = { el: node, marker: new window.mapboxgl.Marker({ element: node, anchor: 'center' }) };
        }
        ghost.el.className = pinClass(r) + ' smap-mk--ghost smap-mk--hl';
        ghost.el.innerHTML = '<span class="smap-mk__pin"></span>';
        ghost.el.style.zIndex = 4;
        ghost.marker.setLngLat(r._pos).addTo(map);
      } else if (ghost) ghost.marker.remove();
    }

    function renderLegend() {
      if (!legend) return;
      var mk = function (cls) { return '<span class="smap-mk ' + cls + '"><span class="smap-mk__pin"></span></span>'; };
      var types = {};
      tabRecords(curTab()).forEach(function (r) { types[r.type] = true; });
      var html = '';
      if (curTab().local) {
        html = '<span>' + mk('') + esc(t('legend_stockist')) + '</span>';
      } else {
        if (types.distributor) html += '<span>' + mk('') + esc(typeLabel('distributor')) + '</span>';
        if (types.online) html += '<span>' + mk('smap-mk--online') + esc(typeLabel('online')) + '</span>';
      }
      legend.innerHTML = html;
      legend.hidden = !html;
    }

    function updateMapData() {
      renderLegend();
      if (!mapReady || !map) return;
      dataVersion++;
      var tab = curTab();
      map.getSource('smap').setData(fc(tab.local ? mapRows() : []));
      map.setLayoutProperty('smap-proxy', 'visibility', tab.local ? 'visible' : 'none');
      updateMarkers();
      syncMarkerStates();
    }

    var motion = function (opts) { return Object.assign(opts, REDUCED.matches ? { duration: 0 } : {}); };

    function applyTabView(animate) {
      if (!mapReady || !map) return;
      if (popup) popup.remove();
      var dur = animate && !REDUCED.matches ? 800 : 0;
      var tab = curTab();
      if (state.origin) { map.flyTo({ center: [state.origin.lng, state.origin.lat], zoom: tab.local ? 10 : 3, duration: dur }); return; }
      var rows = mapRows();
      if (tab.local && state.region && rows.length) {
        var b = new window.mapboxgl.LngLatBounds();
        rows.forEach(function (r) { b.extend(r._pos); });
        map.fitBounds(b, { padding: 60, maxZoom: 12, duration: dur });
        return;
      }
      var v = viewFor(tab);
      map.flyTo({ center: v.center, zoom: v.zoom, duration: dur });
    }

    function openPopup(r) {
      if (!mapReady || !map || !r._pos) return;
      if (popup) popup.remove();
      var node = document.createElement('div');
      node.className = 'smap-pop';
      var where = r.type === 'retailer' ? (r.town_city || r.country) : r.country;
      var d = state.origin && state.dist.has(r.id) ? ' · ' + fmtDist(state.dist.get(r.id)) : '';
      node.innerHTML = '<h3 class="smap-pop__title">' + esc(r.name) + '</h3><p class="smap-pop__meta">' + esc(where) + esc(d) + '</p>' +
        '<button type="button" class="smap__btn smap__btn--primary">' + esc(r.type === 'distributor' ? t('label_details') : t('label_view')) + '</button>';
      node.querySelector('button').addEventListener('click', function (ev) { openSheet(r, ev.currentTarget); });
      var pop = new window.mapboxgl.Popup({ offset: 18, maxWidth: '280px', focusAfterOpen: false, closeOnClick: false, className: 'smap-pop-skin' })
        .setLngLat(r._pos).setDOMContent(node).addTo(map);
      popup = pop;
      popupRecId = r.id;
      pop.on('close', function () {
        if (popup === pop && state.hl === r.id) setHighlight(null); else syncMarkerStates();
      });
      setHighlight(r.id);
    }

    function flyToRecord(r, opener) {
      if (!mapReady || !map || !r._pos) { openSheet(r, opener); return; }   // no map: go straight to the profile
      if (!DESKTOP_MQ.matches) el.mapPane.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
      var zoom = r.type === 'retailer' ? Math.max(map.getZoom(), 13) : Math.max(map.getZoom(), 3);
      map.flyTo(motion({ center: r._pos, zoom: zoom, essential: true }));
      openPopup(r);
    }

    function placeYou() {
      if (!mapReady || !map || !state.origin) return;
      if (!youMarker) {
        var node = document.createElement('div');
        node.className = 'smap-you'; node.style.zIndex = 5;
        node.setAttribute('aria-hidden', 'true');
        node.innerHTML = '<span>' + esc(t('label_you')) + '</span>';
        youMarker = new window.mapboxgl.Marker({ element: node });
      }
      youMarker.setLngLat([state.origin.lng, state.origin.lat]).addTo(map);
    }

    /* ================= Search / location ================= */
    function setOrigin(o) {
      state.origin = o;
      state.dist = new Map();
      if (o) records.forEach(function (r) { if (hasCoords(r)) state.dist.set(r.id, haversineKm(o.lat, o.lng, r.lat, r.lng)); });
      state.region = '';
      render();
      writeHash();
      if (o) placeYou(); else if (youMarker) youMarker.remove();
      applyTabView(true);
    }
    function busy(btn, isBusy) {
      if (!btn) return;
      btn.disabled = isBusy;
      btn.classList.toggle('is-busy', isBusy);
      if (isBusy) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
    }

    on(el.form, 'submit', function (e) {
      e.preventDefault();
      var query = el.input ? el.input.value.trim() : '';
      if (!query) { renderStatus(t('msg_search_empty'), true); if (el.input) el.input.focus(); return; }
      if (!cfg.token) { renderStatus(t('msg_search_failed'), true); return; }
      busy(el.submit, true);
      renderStatus(t('msg_searching'));
      var url = 'https://api.mapbox.com/search/geocode/v6/forward?q=' + encodeURIComponent(query) + '&limit=1' +
        (curTab().local || curTab().countries.indexOf('new zealand') !== -1 ? '&country=nz' : '') + '&access_token=' + encodeURIComponent(cfg.token);
      fetch(url, { signal: ac.signal })
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
        .then(function (json) {
          if (destroyed) return;
          var f = json.features && json.features[0];
          if (!f) { renderStatus(t('msg_not_found', { query: query }), true); return; }
          var c = f.geometry.coordinates;
          var label = (f.properties && (f.properties.name_preferred || f.properties.name)) || query;
          setOrigin({ lat: c[1], lng: c[0], label: label });
        })
        .catch(function (err) {
          if (destroyed) return;
          console.warn('Stockist map: geocoding failed', err);
          renderStatus(t('msg_search_failed'), true);
        })
        .then(function () { if (!destroyed) busy(el.submit, false); });
    });

    on(el.locate, 'click', function () {
      if (!('geolocation' in navigator)) { renderStatus(t('msg_geo_unsupported'), true); return; }
      busy(el.locate, true);
      renderStatus(t('msg_locating'));
      navigator.geolocation.getCurrentPosition(function (pos) {
        if (destroyed) return;
        busy(el.locate, false);
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: t('label_your_location') });
      }, function (err) {
        if (destroyed) return;
        busy(el.locate, false);
        renderStatus(err && err.code === 1 ? t('msg_geo_denied') : t('msg_geo_error'), true);
      }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
    });

    on(el.status, 'click', function (e) {
      if (e.target.closest('[data-act="clear-origin"]')) {
        if (el.input) el.input.value = '';
        setOrigin(null);
        renderStatus();
        if (el.input) el.input.focus();
      }
    });

    /* ================= Profile sheet ================= */
    var sheet = el.sheet, lastFocus = null, closeTimer = null;

    function sheetContent(r) {
      var d = state.origin && state.dist.get(r.id);
      var initial = initialOf(r);
      var media = '';
      if (r.photo) media = '<img class="smap__photo" src="' + esc(r.photo) + '" alt="">';
      else if (r.logo) media = '<div class="smap__plate"><img src="' + esc(r.logo) + '" alt="' + esc(t('label_logo', { name: r.name })) + '" data-initial="' + initial + '"></div>';
      else media = '<span class="smap__initial" aria-hidden="true">' + initial + '</span>';

      var addr = r.type === 'retailer'
        ? (esc(r.address || '') + (r.address ? '<br>' : '') + esc(r.town_city) + (r.town_city ? ', ' : '') + esc(r.country))
        : (esc(r.town_city && r.town_city !== r.country ? r.town_city + ', ' : '') + esc(r.country) + (r.address ? '<br>' + esc(r.address) : ''));
      var brands = r.brands.map(function (b) { return '<span class="smap__brand-chip">' + esc(brandLabel(b)) + '</span>'; }).join('');
      var products = r.products.filter(function (p) { return p && p.title; }).map(function (p) {
        return '<a class="smap__product" href="' + esc(p.url || '#') + '"><span class="smap__product-img">' +
          (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '</span><span class="smap__product-title">' + esc(p.title) + '</span></a>';
      }).join('');
      var blurb = r.blurb ? '<p class="smap__blurb">' + esc(r.blurb) + '</p>'
        : (T.msg_no_blurb ? '<p class="smap__blurb smap__blurb--empty">' + esc(T.msg_no_blurb) + '</p>' : '');
      var body =
        '<div class="smap__sheet-tags"><span>' + esc(typeLabel(r.type)) + '</span></div>' +
        '<h2 class="smap__sheet-title" id="' + sheetTitleId + '">' + esc(r.name) + '</h2>' +
        '<p class="smap__sheet-util">' + esc(r.type === 'retailer' ? (r.town_city || r.country) : r.country) +
          (d ? ' · <span class="smap__km">' + esc(fmtDist(d)) + '</span>' : '') + '</p>' +
        blurb +
        '<dl class="smap__facts">' +
          (addr ? '<div><dt>' + esc(t(r.type === 'retailer' ? 'label_address' : 'label_location')) + '</dt><dd>' + addr + '</dd></div>' : '') +
          (brands ? '<div><dt>' + esc(t('label_brands')) + '</dt><dd>' + brands + '</dd></div>' : '') +
          (products ? '<div><dt>' + esc(t('label_products')) + '</dt><dd><div class="smap__products">' + products + '</div></dd></div>' : '') +
        '</dl>';

      var btns = [];
      var dir = hasDirections(r);
      if (dir) btns.push('<a class="smap__btn smap__btn--primary" href="' + esc(directionsUrl(r)) + '" target="_blank" rel="noopener">' + esc(t('label_directions')) + '</a>');
      // Website: the stockist's brand page when there is one, else its home page.
      if (r.brand_page_url || r.website) {
        btns.push('<a class="smap__btn" data-smap-website href="' + esc(r.brand_page_url || r.website) + '" target="_blank" rel="noopener">' +
          esc(r.brand_page_url ? t('label_brand_page', { brand: T.brand_name }) : t('label_website')) + '</a>');
      }
      if (r.instagram) btns.push('<a class="smap__btn" href="' + esc(r.instagram) + '" target="_blank" rel="noopener">' + esc(t('label_instagram')) + '</a>');
      if (r.email && r.type !== 'retailer') btns.push('<a class="smap__btn" href="mailto:' + esc(r.email) + '">' + esc(t('label_email')) + '</a>');
      if (!dir && btns.length) btns[0] = btns[0].replace('class="smap__btn"', 'class="smap__btn smap__btn--primary"');
      return { media: media, body: body, actions: btns.join('') };
    }

    function openSheet(r, opener) {
      if (!r || !sheet) return;
      clearTimeout(closeTimer);
      if (!sheet.open) lastFocus = opener || document.activeElement;
      var c = sheetContent(r);
      if (el.sheetMedia) el.sheetMedia.innerHTML = c.media;
      if (el.sheetBody) el.sheetBody.innerHTML = c.body;
      if (el.sheetActions) el.sheetActions.innerHTML = c.actions;
      sheet.setAttribute('aria-labelledby', sheetTitleId);
      if (!sheet.open) sheet.showModal();
      void sheet.offsetWidth;                       // commit the closed position so the sheet transitions in
      sheet.classList.add('is-open');
      var scroller = sheet.querySelector('.smap__sheet-scroll');
      if (scroller) scroller.scrollTop = 0;
      if (el.sheetClose) el.sheetClose.focus({ preventScroll: true });
      var hash = '#stockist=' + encodeURIComponent(r.id);
      if (location.hash !== hash) history.replaceState(null, '', hash);
    }

    /* Reflect the region filter in the URL (replaceState: no scroll, no history entry). */
    function writeHash() {
      if (sheet && sheet.open) return;
      var want = state.region ? '#' + state.region : '';
      var cur = location.hash;
      if (want) { if (cur !== want) history.replaceState(null, '', want); }
      else if (/^#stockist=/.test(cur) || isRegion(cur.slice(1))) history.replaceState(null, '', location.pathname + location.search);
    }
    function clearHash() {
      if (/^#stockist=/.test(location.hash)) history.replaceState(null, '', state.region ? '#' + state.region : location.pathname + location.search);
    }
    function finishClose() {
      clearTimeout(closeTimer);
      sheet.classList.remove('is-open');
      if (sheet.open) sheet.close();
      var target = lastFocus && document.contains(lastFocus) ? lastFocus : null;
      lastFocus = null;
      if (target) target.focus({ preventScroll: true });
    }
    function closeSheet() {
      if (!sheet || !sheet.open) return;
      clearHash();
      if (!sheet.classList.contains('is-open')) { finishClose(); return; }
      sheet.classList.remove('is-open');
      if (REDUCED.matches) finishClose(); else closeTimer = setTimeout(finishClose, 280);
    }

    if (sheet) {
      on(el.sheetClose, 'click', closeSheet);
      on(sheet, 'cancel', function (e) { e.preventDefault(); closeSheet(); });
      on(sheet, 'close', function () {       // closed some other way (e.g. a second Esc): tidy up
        clearTimeout(closeTimer);
        sheet.classList.remove('is-open');
        clearHash();
        var target = lastFocus && document.contains(lastFocus) ? lastFocus : null;
        lastFocus = null;
        if (target) target.focus({ preventScroll: true });
      });
      var downOnBackdrop = false;
      var outside = function (e) {
        if (e.target !== sheet) return false;
        var b = sheet.getBoundingClientRect();
        return e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom;
      };
      on(sheet, 'pointerdown', function (e) { downOnBackdrop = outside(e); });
      on(sheet, 'click', function (e) { if (downOnBackdrop && outside(e)) closeSheet(); downOnBackdrop = false; });
      on(sheet, 'keydown', function (e) {
        if (e.key !== 'Tab') return;
        var f = Array.prototype.slice.call(sheet.querySelectorAll('a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])'))
          .filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === sheet)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    function tabFor(r) {
      if (matchesTab(r, curTab())) return curTab();
      return tabs.filter(function (x) { return matchesTab(r, x); })[0] || null;
    }
    function openFromHash(onLoad) {
      var h = onLoad ? parseLoad() : parseHash();
      if (!h.stockist) {
        if (sheet && sheet.open) closeSheet();
        if (h.tab) selectTab(h.tab);
        else if (h.region && h.region !== state.region) selectRegion(h.region);
        return;
      }
      var r = BY_ID.get(h.stockist);
      if (!r) return;
      var tab = tabFor(r);
      if (tab && tab.key !== state.tab) { state.tab = tab.key; state.region = ''; state.hl = null; render(); applyTabView(false); }
      openSheet(r, null);
    }
    on(window, 'hashchange', function () { openFromHash(false); });

    /* ================= Events ================= */
    function selectTab(key, focus) {
      var tab = tabByKey(key);
      if (!tab || key === state.tab) { if (focus && tab && tab.el) tab.el.focus(); return; }
      state.tab = key; state.hl = null; state.region = '';
      writeHash();
      render(); applyTabView(true);
      if (focus && tab.el) tab.el.focus();
    }
    function selectRegion(key) {
      if (key && !curTab().local) {
        var lt = firstLocalTab();
        if (!lt) return;
        state.tab = lt.key; state.hl = null;
        state.region = key;
        render();
      } else {
        state.region = key;
        renderControls(); renderList(); updateMapData();
      }
      applyTabView(true);
      writeHash();
    }
    on(el.tabs, 'click', function (e) { var b = e.target.closest('[data-tab]'); if (b) selectTab(b.dataset.tab); });
    on(el.tabs, 'keydown', function (e) {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(e.key) === -1) return;
      e.preventDefault();
      var keys = tabs.map(function (x) { return x.key; });
      var i = keys.indexOf(state.tab);
      var next = e.key === 'Home' ? 0 : e.key === 'End' ? keys.length - 1
        : (i + (e.key === 'ArrowRight' ? 1 : -1) + keys.length) % keys.length;
      selectTab(keys[next], true);
    });
    on(el.regions, 'click', function (e) {
      var c = e.target.closest('[data-region]');
      if (c) selectRegion(c.dataset.region);
    });
    on(el.showAll, 'click', function () { selectRegion(''); });

    on(el.list, 'click', function (e) {
      var row = e.target.closest('[data-stockist-id]');
      if (!row) return;
      var r = BY_ID.get(row.getAttribute('data-stockist-id'));
      if (!r) return;
      var details = e.target.closest('[data-smap-details]');
      if (details) { openSheet(r, details); return; }
      if (e.target.closest('a, button, input')) return;   // name, website and directions links keep their default
      flyToRecord(r, row.querySelector('[data-smap-details]'));
    });
    var rowIdAt = function (e) { var c = e.target.closest('[data-stockist-id]'); return c ? c.getAttribute('data-stockist-id') : null; };
    on(el.list, 'mouseover', function (e) { var id = rowIdAt(e); if (id && id !== state.hl) setHighlight(id); });
    on(el.list, 'mouseleave', function () { setHighlight(popupId()); });
    on(el.list, 'focusin', function (e) { var id = rowIdAt(e); if (id) setHighlight(id); });

    /* Controls height: the desktop map pulls up beside the controls, and the list header sticks below them. */
    var ro = null;
    var setControlsH = function () {
      if (!el.controls) return;
      root.style.setProperty('--smap-controls-h', (DESKTOP_MQ.matches ? el.controls.offsetHeight : 0) + 'px');
    };
    if (el.controls && 'ResizeObserver' in window) {
      ro = new ResizeObserver(function () { setControlsH(); if (map && mapReady) map.resize(); });
      ro.observe(el.controls);
    }
    on(DESKTOP_MQ, 'change', setControlsH);
    setControlsH();

    /* Sticky theme header (Dawn and friends): our sticky controls and map sit below it. */
    var HEADER_SEL = '.shopify-section-header-sticky, .section-header.shopify-section-header-sticky, [data-sticky-header], .header--sticky, [is="sticky-header"], sticky-header';
    var stickyAncestor = function (el) {
      /* The sticky position may sit on the header element or on its section wrapper. */
      for (var n = el, i = 0; n && n !== document.body && i < 4; n = n.parentElement, i++) {
        var pos = getComputedStyle(n).position;
        if (pos === 'sticky' || pos === 'fixed') return n;
      }
      return null;
    };
    var setStickyTop = function () {
      var h = document.querySelector(HEADER_SEL), px = 0;
      if (h) {
        var type = (h.getAttribute('data-sticky-type') || h.getAttribute('sticky-type') || 'always').toLowerCase();
        var s = stickyAncestor(h);
        /* Only an always-visible sticky header needs an offset; a hide-on-scroll header leaves the top free. */
        if (s && type !== 'none' && type !== 'on-scroll-up' && type !== 'reduce-logo-size' && type !== 'hide-on-scroll') px = Math.round(s.getBoundingClientRect().height);
      }
      root.style.setProperty('--smap-sticky-top', px + 'px');
    };
    setStickyTop();
    on(window, 'resize', setStickyTop);

    /* ================= Mobile List/Map toggle ================= */
    var toggle = null, toggleIo = null, rootIo = null, mapInView = true;
    function setToggle() {
      if (!toggle) return;
      toggle.querySelector('.smap__toggle-label').textContent = mapInView ? t('label_list') : t('label_map');
      toggle.querySelector('svg').innerHTML = mapInView ? ICON_LIST : ICON_MAP;
      toggle.setAttribute('aria-label', mapInView ? t('label_jump_list') : t('label_jump_map'));
    }
    function createToggle() {
      if (toggle || mapFailed || !MOBILE_MQ.matches || !el.mapPane || !el.panel) return;
      toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'smap__toggle';
      toggle.setAttribute('aria-controls', el.panel.id || '');
      toggle.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true" focusable="false"></svg><span class="smap__toggle-label"></span>';
      toggle.hidden = true;
      toggle.addEventListener('click', function () {
        var target = mapInView ? el.panel : el.mapPane;
        target.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
      });
      root.appendChild(toggle);
      setToggle();
      if ('IntersectionObserver' in window) {
        toggleIo = new IntersectionObserver(function (entries) {
          mapInView = entries[0].intersectionRatio > 0.35;
          setToggle();
        }, { threshold: [0, 0.35, 1] });
        toggleIo.observe(el.mapPane);
        // Only float the button while the section itself is on screen.
        rootIo = new IntersectionObserver(function (entries) {
          if (toggle) toggle.hidden = !entries[0].isIntersecting;
        });
        rootIo.observe(el.panel.parentNode || root);
      } else {
        toggle.hidden = false;
      }
    }
    function removeToggle() {
      if (toggleIo) toggleIo.disconnect();
      if (rootIo) rootIo.disconnect();
      toggleIo = rootIo = null;
      if (toggle) toggle.remove();
      toggle = null;
    }
    on(MOBILE_MQ, 'change', function () { if (MOBILE_MQ.matches) createToggle(); else removeToggle(); });

    /* ================= Boot ================= */
    render();
    createToggle();
    initMap();
    openFromHash(true);

    return {
      destroy: function () {
        destroyed = true;
        ac.abort();
        clearTimeout(failTimer);
        clearTimeout(closeTimer);
        if (ro) ro.disconnect();
        removeToggle();
        if (popup) { try { popup.remove(); } catch (e) { /* ignore */ } }
        if (map) { try { map.remove(); } catch (e) { /* ignore */ } }
        map = null; mapReady = false; popup = null; youMarker = null; ghost = null;
        onScreen.clear(); pinCache.clear();
        if (legend) legend.remove();
        if (sheet && sheet.open) sheet.close();
        instances.delete(root);
      }
    };
  }

  function init(root) {
    if (!root || instances.has(root)) return;
    try { instances.set(root, createInstance(root)); }
    catch (e) { console.error('Stockist map failed to start', e); }
  }
  function destroy(root) {
    var inst = root && instances.get(root);
    if (inst) inst.destroy();
  }

  function start() {
    var boot = function () { Array.prototype.forEach.call(document.querySelectorAll('[data-smap]'), init); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
    document.addEventListener('shopify:section:load', function (e) { init(e.target.querySelector('[data-smap]')); });
    document.addEventListener('shopify:section:unload', function (e) { destroy(e.target.querySelector('[data-smap]')); });
  }
  if (typeof document !== 'undefined' && typeof window !== 'undefined') start();

  if (typeof module !== 'undefined') module.exports = { haversineKm: haversineKm, regionFor: regionFor, fillTemplate: fillTemplate, parseBoxes: parseBoxes };
})();
